import { useData } from "@/contexts/DataContext";
import { useLocation } from "wouter";
import { FileText, FileSpreadsheet, ChevronLeft, Download, Calendar, History } from "lucide-react";
import { generatePDF, generateExcel } from "@/lib/reports";
import { format } from "date-fns";

export default function MyReports() {
  const [, setLocation] = useLocation();
  const { reports, transactions, products, businessProfile, isLoading } = useData();

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/rewards"); // Taking them back to rewards or settings
    }
  };

  const reGenerateReport = async (report: any) => {
    const start = new Date(report.startDate);
    const end = new Date(report.endDate);

    const filteredTransactions = transactions.filter((t) => {
      // Cancelled GST invoices are preserved for compliance but excluded from sales/expense reports.
      if ((t as any).cancelled) return false;
      const date = new Date(t.createdAt || t.timestamp);
      return date >= start && date <= end;
    });

    const reportData = {
      transactions: filteredTransactions,
      startDate: start,
      endDate: end,
      businessName: businessProfile?.businessName || "DailyHisaab Business",
      businessAddress: businessProfile?.businessAddress,
      businessPhone: businessProfile?.businessPhone,
      businessEmail: businessProfile?.businessEmail,
      businessLogo: businessProfile?.businessLogo,
      businessGstin: businessProfile?.gstin,
    };

    if (report.type === "PDF") {
      await generatePDF(reportData);
    } else {
      await generateExcel(reportData);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614]">
      <header className="px-4 py-4 flex items-center sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md">
        <button onClick={handleBack} className="w-10 h-10 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-sm border border-slate-100 dark:border-slate-700 active:scale-95 transition-transform shrink-0">
          <ChevronLeft className="w-6 h-6 text-slate-800 dark:text-white" />
        </button>
        <h1 className="text-lg font-black text-slate-900 dark:text-white ml-4 tracking-tight">Report History</h1>
      </header>

      <div className="flex-1 px-4 py-6 pb-24 overflow-y-auto">
        {reports.length === 0 ? (
          <div className="py-20 text-center">
            <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <History className="h-10 w-10 text-slate-300" />
            </div>
            <p className="text-slate-400 font-bold">No reports generated yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report: any) => (
              <div key={report.id} className="bg-white dark:bg-slate-900 rounded-[2rem] p-5 shadow-sm border border-slate-100 dark:border-slate-800 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${report.type === 'PDF' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                  {report.type === 'PDF' ? <FileText className="h-6 w-6" /> : <FileSpreadsheet className="h-6 w-6" />}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 dark:text-white truncate">{report.rangeLabel}</h3>
                  <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                    <Calendar className="h-3 w-3" />
                    <span>Generated {format(new Date(report.createdAt), "dd MMM, hh:mm a")}</span>
                  </div>
                </div>

                <button
                  onClick={() => reGenerateReport(report)}
                  className="w-10 h-10 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center text-[#8B1A1A] active:scale-90 transition-transform"
                >
                  <Download className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}